import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import {
	ArrowLeft,
	Car,
	CheckCircle,
	Download,
	LogIn,
	Monitor,
} from "lucide-react";
import { useState } from "react";
import { getDb } from "@/db";
import { cars, events, heatAssignments, heats, runs } from "@/db/schema";
import { authClient } from "@/lib/auth-client";
import { getSession } from "@/lib/auth-session";
import { computeScores, formatScore } from "@/lib/scoring";

const getEventWithData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();

		if (!event) throw new Error("Event not found");

		// Draft events require admin/operator role
		const session = await getSession();
		const role = (session?.user as { role?: string } | undefined)?.role;
		const isAdmin = role === "admin" || role === "operator";

		if (event.visibility === "draft" && !isAdmin) {
			throw new Error("Event not found");
		}

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		const eventRuns = await db
			.select()
			.from(runs)
			.where(and(eq(runs.eventId, event.id), eq(runs.status, "valid")));

		const eventHeats = await db
			.select()
			.from(heats)
			.where(eq(heats.eventId, event.id));

		const allAssignments = [];
		for (const heat of eventHeats) {
			const ha = await db
				.select()
				.from(heatAssignments)
				.where(eq(heatAssignments.heatId, heat.id));
			allAssignments.push(...ha);
		}

		return {
			event,
			cars: eventCars,
			runs: eventRuns,
			heats: eventHeats,
			assignments: allAssignments,
		};
	});

// Separate server function for user's car — called once on load, not polled
const getUserCarForEvent = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const session = await getSession();
		const userId = session?.user?.id ?? null;
		if (!userId) return null;

		const db = getDb();
		const userCar = await db
			.select()
			.from(cars)
			.where(and(eq(cars.eventId, Number(eventId)), eq(cars.userId, userId)))
			.get();

		return userCar
			? {
					id: userCar.id,
					carName: userCar.carName,
					carNumber: userCar.carNumber,
					driverName: userCar.driverName,
					checkedIn: userCar.checkedIn,
				}
			: null;
	});

export const Route = createFileRoute("/events/$eventId/")({
	loader: ({ params }) =>
		Promise.all([
			getEventWithData({ data: params.eventId }),
			getUserCarForEvent({ data: params.eventId }),
		]).then(([eventData, userCar]) => ({ ...eventData, userCar })),
	component: EventDetailPage,
});

function formatTime(ms: number | null): string {
	if (ms == null) return "--";
	return `${(ms / 1000).toFixed(3)}s`;
}

function EventDetailPage() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();

	const { userCar, ...eventInitialData } = initialData;

	const { data } = useQuery({
		queryKey: ["event", params.eventId],
		queryFn: () => getEventWithData({ data: params.eventId }),
		initialData: eventInitialData,
		refetchInterval: 5000,
	});

	const {
		event,
		cars: eventCars,
		runs: eventRuns,
		heats: eventHeats,
		assignments,
	} = data;
	const { data: session } = authClient.useSession();
	const divisions: string[] = (() => {
		try {
			return JSON.parse(event.divisions);
		} catch {
			return [];
		}
	})();
	const [activeDivision, setActiveDivision] = useState<string | null>(null);

	const carData = eventCars.map((c) => ({
		id: c.id,
		carNumber: c.carNumber,
		carName: c.carName,
		driverName: c.driverName,
		avatarUrl: c.avatarUrl,
		photoUrl: c.photoUrl,
		division: c.division,
	}));
	const runData = eventRuns.map((r) => ({
		carId: r.carId,
		elapsedTimeMs: r.elapsedTimeMs,
		lane: r.lane,
		heatId: r.heatId,
	}));
	const heatData = eventHeats.map((h) => ({
		id: h.id,
		status: h.status,
		phase: h.phase,
	}));
	const assignmentData = assignments.map((a) => ({
		heatId: a.heatId,
		lane: a.lane,
		carId: a.carId,
	}));

	const isFinals = event.status === "finals";
	const finalsScoring = event.finalsScoringMethod || event.scoringMethod;

	const scores = computeScores(
		carData,
		runData,
		isFinals ? finalsScoring : event.scoringMethod,
		{
			heats: heatData,
			assignments: assignmentData,
			laneCount: event.laneCount,
			phase: isFinals ? "finals" : undefined,
		},
	);

	const filteredScores = activeDivision
		? scores.filter((s) => s.car.division === activeDivision)
		: scores;

	// For finals, also compute qualifying scores to show below
	const qualifyingScores = isFinals
		? computeScores(carData, runData, event.scoringMethod, {
				heats: heatData,
				assignments: assignmentData,
				laneCount: event.laneCount,
				phase: "qualifying",
			})
		: null;

	const filteredQualifyingScores = qualifyingScores
		? activeDivision
			? qualifyingScores.filter((s) => s.car.division === activeDivision)
			: qualifyingScores
		: null;

	const isLive = event.status === "qualifying" || event.status === "finals";

	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			<div className="max-w-5xl mx-auto">
				<Link
					to="/events"
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					All Events
				</Link>

				{/* Event image */}
				{event.imageUrl && (
					<div className="mb-6">
						<img
							src={event.imageUrl}
							alt={event.name}
							className="max-h-48 w-full object-cover rounded-xl"
						/>
					</div>
				)}

				{/* Event header */}
				<div className="text-center mb-8">
					<div className="flex items-center justify-center gap-3 mb-2">
						{isLive && (
							<span className="flex items-center gap-1.5 px-3 py-1 bg-success text-foreground text-xs font-bold uppercase rounded-full animate-pulse">
								<span className="w-2 h-2 bg-success/50 rounded-full" />
								Live
							</span>
						)}
					</div>
					<h1 className="text-4xl sm:text-5xl text-foreground">{event.name}</h1>
					<p className="text-muted-foreground mt-2 text-lg">
						{event.date}
						{event.location && ` \u2022 ${event.location}`}
					</p>
					<div className="flex items-center justify-center gap-6 mt-3 text-sm text-muted-foreground">
						<span>{event.laneCount} lanes</span>
						<span>{scores.length} cars</span>
						<span className="capitalize">
							{event.scoringMethod.replace("_", " ")} scoring
						</span>
					</div>
					<div className="flex items-center justify-center gap-3 mt-4">
						<Link
							to="/events/$eventId/tv"
							params={{ eventId: String(event.id) }}
							className="inline-flex items-center gap-2 px-4 py-2 bg-card hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition-colors text-sm"
						>
							<Monitor size={16} />
							TV Mode
						</Link>
						<a
							href={`/api/export?eventId=${event.id}&type=standings`}
							download
							className="inline-flex items-center gap-2 px-4 py-2 bg-card hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition-colors text-sm"
						>
							<Download size={16} />
							Export CSV
						</a>
					</div>
				</div>

				{/* Registration CTA */}
				{(() => {
					const registrationOpen =
						(event.status === "setup" || event.status === "qualifying") &&
						event.registrationMode === "open";
					if (userCar) {
						return (
							<Link
								to="/events/$eventId/register"
								params={{ eventId: String(event.id) }}
								className="block bg-success/10 border border-success/30 rounded-xl p-4 mb-6 hover:bg-success/15 transition-colors"
							>
								<div className="flex items-center gap-3">
									<CheckCircle size={20} className="text-success shrink-0" />
									<div className="flex-1 min-w-0">
										<p className="text-foreground font-bold">
											You're registered!
										</p>
										<p className="text-muted-foreground text-sm">
											#{userCar.carNumber}{" "}
											{userCar.carName ?? userCar.driverName}
											{userCar.checkedIn
												? " — Checked in"
												: " — Not yet checked in"}
										</p>
									</div>
									<span className="text-muted-foreground text-sm">View →</span>
								</div>
							</Link>
						);
					}
					if (registrationOpen && session?.user) {
						return (
							<Link
								to="/events/$eventId/register"
								params={{ eventId: String(event.id) }}
								className="block bg-primary/10 border border-primary/30 rounded-xl p-4 mb-6 hover:bg-primary/15 transition-colors text-center"
							>
								<Car size={24} className="text-primary mx-auto mb-1" />
								<p className="text-foreground font-bold">Register Your Car</p>
								<p className="text-muted-foreground text-sm">
									Sign up to race in this event
								</p>
							</Link>
						);
					}
					if (registrationOpen && !session?.user) {
						return (
							<Link
								to="/login"
								search={{
									redirect: `/events/${event.id}/register`,
								}}
								className="block bg-card border border-border rounded-xl p-4 mb-6 hover:border-primary/40 transition-colors text-center"
							>
								<LogIn size={24} className="text-primary mx-auto mb-1" />
								<p className="text-foreground font-bold">Sign in to Register</p>
								<p className="text-muted-foreground text-sm">
									Create an account to enter your car
								</p>
							</Link>
						);
					}
					return null;
				})()}

				{/* Leaderboard */}
				{isFinals && (
					<h2 className="text-xl text-foreground mb-4 font-bold uppercase text-center">
						Finals Leaderboard
					</h2>
				)}
				{/* Division tabs */}
				{divisions.length > 0 && (
					<div className="flex gap-2 mb-4 justify-center flex-wrap">
						<button
							type="button"
							onClick={() => setActiveDivision(null)}
							className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
								activeDivision == null
									? "bg-primary text-primary-foreground"
									: "bg-card/50 text-muted-foreground hover:text-foreground"
							}`}
						>
							All
						</button>
						{divisions.map((div) => (
							<button
								key={div}
								type="button"
								onClick={() => setActiveDivision(div)}
								className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
									activeDivision === div
										? "bg-primary text-primary-foreground"
										: "bg-card/50 text-muted-foreground hover:text-foreground"
								}`}
							>
								{div}
							</button>
						))}
					</div>
				)}
				{filteredScores.length === 0 ? (
					<div className="bg-card rounded-xl p-12 text-center">
						<Car size={48} className="text-muted-foreground mx-auto mb-4" />
						<p className="text-foreground text-lg">No cars registered yet</p>
					</div>
				) : (
					<>
						{/* Top 3 podium */}
						{filteredScores[0]?.score != null && (
							<div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 max-w-2xl mx-auto">
								{[1, 0, 2].map((podiumIdx) => {
									const entry = filteredScores[podiumIdx];
									if (!entry || entry.score == null)
										return <div key={podiumIdx} />;
									const isFirst = podiumIdx === 0;
									return (
										<div
											key={entry.car.id}
											className={`text-center ${isFirst ? "order-1 sm:-mt-4" : podiumIdx === 1 ? "order-0" : "order-2"}`}
										>
											<div
												className={`rounded-xl p-3 sm:p-4 border ${
													isFirst
														? "bg-gold/20 border-gold/50"
														: podiumIdx === 1
															? "bg-silver/10 border-silver/30"
															: "bg-bronze/20 border-bronze/30"
												}`}
											>
												<div
													className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-black mb-2 ${
														isFirst
															? "bg-gold text-primary-foreground"
															: podiumIdx === 1
																? "bg-silver text-primary-foreground"
																: "bg-bronze text-foreground"
													}`}
												>
													{podiumIdx + 1}
												</div>
												{entry.car.avatarUrl && (
													<img
														src={entry.car.avatarUrl}
														alt={entry.car.carName ?? "Car"}
														className="w-16 h-16 rounded-lg mx-auto mb-2 object-cover"
													/>
												)}
												<p className="text-foreground font-bold text-sm sm:text-base truncate">
													{entry.car.carName ?? `Car #${entry.car.carNumber}`}
												</p>
												<p className="text-muted-foreground text-xs sm:text-sm truncate">
													{entry.car.driverName}
												</p>
												{entry.car.division && (
													<span className="inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded mt-1">
														{entry.car.division}
													</span>
												)}
												<p className="text-primary font-display font-bold text-lg sm:text-xl mt-1">
													{formatScore(entry)}
												</p>
											</div>
										</div>
									);
								})}
							</div>
						)}

						{/* Full results table */}
						<div className="bg-card/50 border-2 border-border rounded-xl overflow-hidden">
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr>
											<th className="px-4 py-3">Pos</th>
											<th className="px-4 py-3">Car</th>
											<th className="px-4 py-3 hidden sm:table-cell">Driver</th>
											<th className="px-4 py-3 text-right">Best</th>
											<th className="px-4 py-3 text-right hidden sm:table-cell">
												Runs
											</th>
											<th
												className="px-4 py-3 text-right"
												title={`Scored by ${isFinals ? (finalsScoring === "best" ? "best time" : finalsScoring === "average" ? "average time" : finalsScoring === "drop_worst" ? "drop worst" : finalsScoring === "points" ? "points (lower is better)" : finalsScoring) : event.scoringMethod === "best" ? "best time" : event.scoringMethod === "average" ? "average time" : event.scoringMethod === "drop_worst" ? "drop worst" : event.scoringMethod === "points" ? "points (lower is better)" : event.scoringMethod}`}
											>
												Score
											</th>
										</tr>
									</thead>
									<tbody>
										{filteredScores.map((entry, i) => (
											<tr
												key={entry.car.id}
												className={i < 3 ? "bg-primary/5" : ""}
											>
												<td className="px-4 py-3">
													<span
														className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-black ${
															i === 0
																? "bg-gold text-primary-foreground"
																: i === 1
																	? "bg-silver text-primary-foreground"
																	: i === 2
																		? "bg-bronze text-foreground"
																		: "bg-muted text-muted-foreground"
														}`}
													>
														{i + 1}
													</span>
												</td>
												<td className="px-4 py-3">
													<Link
														to="/events/$eventId/cars/$carId"
														params={{
															eventId: String(event.id),
															carId: String(entry.car.id),
														}}
														className="flex items-center gap-3"
													>
														{entry.car.avatarUrl ? (
															<img
																src={entry.car.avatarUrl}
																alt=""
																className="w-8 h-8 rounded object-cover hidden sm:block"
															/>
														) : (
															<div className="w-8 h-8 rounded bg-muted items-center justify-center text-muted-foreground text-xs font-bold hidden sm:flex">
																{entry.car.carName?.charAt(0) ?? "#"}
															</div>
														)}
														<div>
															<p className="font-bold text-foreground hover:text-primary transition-colors">
																<span className="inline-flex items-center text-xs font-bold text-primary bg-primary/10 rounded px-1 py-0.5 mr-1">
																	#{entry.car.carNumber}
																</span>
																{entry.car.carName}
															</p>
															<p className="text-muted-foreground text-xs sm:hidden">
																{entry.car.driverName}
																{entry.car.division && (
																	<span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
																		{entry.car.division}
																	</span>
																)}
															</p>
														</div>
													</Link>
												</td>
												<td className="px-4 py-3 text-foreground/80 hidden sm:table-cell">
													{entry.car.driverName}
													{entry.car.division && (
														<span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
															{entry.car.division}
														</span>
													)}
												</td>
												<td className="px-4 py-3 text-right text-foreground font-display text-sm">
													{formatTime(entry.bestTime)}
												</td>
												<td className="px-4 py-3 text-right text-muted-foreground text-sm hidden sm:table-cell">
													{entry.runCount}/{event.attemptsAllowed}
												</td>
												<td className="px-4 py-3 text-right">
													<span className="text-primary font-display font-bold text-lg">
														{formatScore(entry)}
													</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>

						{isLive && (
							<p className="text-center text-muted-foreground text-xs mt-4 animate-pulse">
								Auto-updating every 5 seconds
							</p>
						)}

						{/* Qualifying results when in finals */}
						{filteredQualifyingScores?.some((s) => s.score != null) && (
							<div className="mt-8 pt-6 border-t border-border">
								<h2 className="text-xl text-foreground mb-4">
									Qualifying Results
								</h2>
								<div className="bg-card/50 border-2 border-border rounded-xl overflow-hidden">
									<div className="overflow-x-auto">
										<table className="w-full">
											<thead>
												<tr>
													<th className="px-4 py-3">Pos</th>
													<th className="px-4 py-3">Car</th>
													<th className="px-4 py-3 text-right">Score</th>
												</tr>
											</thead>
											<tbody>
												{filteredQualifyingScores.map((entry, i) => (
													<tr key={entry.car.id}>
														<td className="px-4 py-2 text-muted-foreground text-sm">
															{i + 1}
														</td>
														<td className="px-4 py-2 text-foreground text-sm">
															<span className="text-primary text-xs mr-1">
																#{entry.car.carNumber}
															</span>
															{entry.car.carName ?? entry.car.driverName}
														</td>
														<td className="px-4 py-2 text-right text-foreground font-display text-sm">
															{formatScore(entry)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
